import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import {
  focusTrap, DropdownSimple, AriaLiveRegion, getTranslation,
} from '@resy/ui-kitchen';
import EventsMultiStateSwitch from '../EventsMultiStateSwitch';
import EventsSearchPill from '../EventsSearchPill';
import {
  ALL_EVENTS, ALL_MONTHS, ALL_AVAILABLE, COMING_SOON,
  CITY_FILTER, FILTER_EVENTS,
} from '../../ExperientialEvents';
import './AmexExperientialEventsFilter.scss';

const AmexExperientialEventsFilter = (props) => {
  const {
    matchMedia, handleFilterValue, eventsLocations, eventsMonths,
  } = props;

  const [selectedLocation, setSelectedLocation] = useState({ code: 'ALL', name: 'All Cities' });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [ariaFilterValue, setAriaFilterValue] = useState('');

  const defaultSelectedFilter = [{
    type: 'events',
    data: {
      label: ALL_EVENTS,
      value: 0,
    },
  },
  {
    type: 'months',
    data: {
      key: '',
      value: '',
      orderId: 0,
      label: ALL_MONTHS,
    },
  }];
  const [selectedFilterValues, setSelectedFilterValues] = useState(defaultSelectedFilter);

  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const [filterSelectorOpen, setFilterSelectorOpen] = useState(false);
  const [numberOfFilter, setNumberOfFilter] = useState(0);
  const [displayFilterValues, setDisplayFilterValues] = useState('');
  const toggleLocationSelector = async () => {
    if (locationSelectorOpen) {
      focusTrap.focusRemove();
      if (!matchMedia.md) document.body.classList.remove('prevent-scroll');
    } else {
      focusTrap.focusModal('.EventsSearchPillMenu');
      if (!matchMedia.md) document.body.classList.add('prevent-scroll');
    }

    setLocationSelectorOpen(!locationSelectorOpen);
  };

  const toggleFilterSelector = async () => {
    if (filterSelectorOpen) {
      focusTrap.focusRemove();
      if (!matchMedia.md) document.body.classList.remove('prevent-scroll');
    } else {
      focusTrap.focusModal('.EventsSearchPillMenu');
      if (!matchMedia.md) document.body.classList.add('prevent-scroll');
    }

    setFilterSelectorOpen(!filterSelectorOpen);
  };

  const closeEverything = () => {
    setLocationSelectorOpen(false);
    focusTrap.focusRemove();
    document.body.classList.remove('prevent-scroll');
  };

  // Month filter
  const getMonthOptions = () => {
    const MONTH_OPTIONS = [];
    const MONTH_OPTIONS_NEW = [{
      key: '',
      value: '',
      orderId: 0,
      label: ALL_MONTHS,
    }];
    const MONTH_LABELS = moment().localeData().months();

    const padZero = (num) => (num < 10 ? '0' : '') + num;

    for (let i = 0; i < 12; i += 1) {
      const str = padZero(i + 1);
      const label = MONTH_LABELS[i];
      MONTH_OPTIONS.push({
        key: str, value: str, label, orderId: i,
      });
    }

    MONTH_OPTIONS.forEach((element) => {
      if (eventsMonths.find((eventMon) => element.label === eventMon.label)) {
        MONTH_OPTIONS_NEW.push(element);
      }
    });
    const sortedMonths = MONTH_OPTIONS_NEW.sort((a, b) => a.orderId - b.orderId);
    return sortedMonths;
  };

  const citiesToDisplay = eventsLocations?.slice(0, 10);

  const filterData = [{
    type: 'events',
    data: [{
      label: ALL_EVENTS,
      value: 0,
    },
    {
      label: ALL_AVAILABLE,
      value: 1,
    },
    {
      label: COMING_SOON,
      value: 2,
    }],
  },
  {
    type: 'months',
    data: getMonthOptions(),
  },
  {
    type: 'cities',
    data: citiesToDisplay,
  }];

  const findUsedFilters = (filterValue) => {
    let city = ''; let month = ''; let
      event = '';

    filterData.forEach((itemData) => {
      if (itemData.type === 'events') {
        itemData.data.forEach((itemEvent) => {
          if (filterValue.status === '') {
            event = ALL_EVENTS;
          } else if (itemEvent.value === filterValue.status) {
            event = itemEvent.label;
          }
        });
      }

      if (itemData.type === 'months') {
        itemData.data.forEach((itemEvent) => {
          if (filterValue.month === -1 && itemEvent.value === '') {
            month = ALL_MONTHS;
          } else if (itemEvent.orderId === filterValue.month) {
            month = itemEvent.label;
          }
        });
      }

      if (itemData.type === 'cities') {
        itemData.data.forEach((itemEvent) => {
          if (!filterValue.slug) {
            city = 'All Cities';
          } else if (itemEvent.url_slug === filterValue.slug) {
            city = itemEvent.name;
          }
        });
      }
    });

    setAriaFilterValue(`Filter values are ${city} , ${event} , ${month}`);
  };

  useEffect(() => {
    const filterValue = { status: 0, month: -1, slug: undefined };
    findUsedFilters(filterValue);
  }, []);

  const handleLocationSelect = async (location) => {
    const selLocation = {
      name: location?.name,
      code: location?.code,
      slug: location?.url_slug,
    };

    setSelectedLocation(selLocation);
    const filterValue = {
      slug: location?.url_slug,
      month: selectedMonth - 1,
      status: selectedStatus,
    };
    handleFilterValue(filterValue);
    findUsedFilters(filterValue);
    closeEverything();
  };

  const handleMonthDropdown = (e) => {
    const filterValue = {
      month: e.target.value - 1,
      status: selectedStatus,
      slug: selectedLocation?.slug,
    };
    handleFilterValue(filterValue);
    findUsedFilters(filterValue);
    setSelectedMonth(e.target.value);
  };

  const handleStatusFilter = (value) => {
    setSelectedStatus(value);
    const filterValue = {
      status: value,
      month: selectedMonth - 1,
      slug: selectedLocation?.slug,
    };
    handleFilterValue(filterValue);
    findUsedFilters(filterValue);
  };

  const selectMonth = () => (
    <DropdownSimple
      name="month"
      id="monthselect"
      onChange={handleMonthDropdown}
      options={getMonthOptions()}
      value={selectedMonth}
      ariaLabel={getTranslation({
        translationKey: 'month',
        fallback: 'Month',
      })}
    />
  );

  const handleFilterSelect = (filterValue, type) => {
    const existingFilterValues = selectedFilterValues?.filter((labelItem) => labelItem.type !== type);
    const newObject = {
      type,
      data: filterValue.data,
    };
    const newSelectedArr = [newObject, ...existingFilterValues];
    setSelectedFilterValues(newSelectedArr);
  };

  const onClickFilterEvents = () => {
    let iCount = 0;
    let selMonth = 0;
    let status = '';
    let displayFilterVal = '';
    const displayFilterArr = [];
    selectedFilterValues.sort((a, b) => b.type - a.type).forEach((newSelectedItem) => {
      if (![ALL_MONTHS, ALL_EVENTS].includes(newSelectedItem.data.label)) {
        iCount += 1;
        displayFilterArr.push(newSelectedItem.data.label);
      }
      if (newSelectedItem.type === 'months') {
        selMonth = newSelectedItem.data.value;
      }

      if (newSelectedItem.type === 'events') {
        status = newSelectedItem.data.value;
      }
    });

    displayFilterArr.forEach((filterDisItem, index) => {
      let delimiter = '';
      if (index !== displayFilterArr.length - 1) {
        delimiter = ', ';
      }
      displayFilterVal = displayFilterVal + filterDisItem + delimiter;
    });

    setNumberOfFilter(iCount);
    if (iCount > 0) {
      setDisplayFilterValues(`Filter: ${displayFilterVal}`);
    } else {
      setDisplayFilterValues(null);
    }

    const filterEvents = {
      status,
      month: selMonth - 1,
      slug: selectedLocation?.slug,
    };

    setSelectedMonth(selMonth);
    setSelectedStatus(status);
    handleFilterValue(filterEvents);
    toggleFilterSelector();
  };

  const renderDesktop = () => (
    <div className="AmexExperientialEventsFilter">
      <ul className="AmexExperientialEventsFilter__citypanel">
        {(citiesToDisplay?.length > 0) && (
          <>
            <li key="EventsSearchPill">
              <EventsSearchPill
                onToggle={toggleLocationSelector}
                onLocationSelect={handleLocationSelect}
                selectedFilterValues={selectedLocation}
                isOpen={locationSelectorOpen}
                filterData={filterData}
                typeOfFilter={CITY_FILTER}
              />
            </li>
          </>
        )}
      </ul>
      <div
        aria-hidden
        className={cx('AmexExperientialEventsFilter AmexExperientialEventsFilter__citypanel__backdrop', {
          open: locationSelectorOpen,
        })}
        onClick={closeEverything}
      />
      <EventsMultiStateSwitch onChangeValue={handleStatusFilter} filterData={filterData} />
      {selectMonth()}

      <AriaLiveRegion>{ariaFilterValue}</AriaLiveRegion>
    </div>
  );

  const renderMobile = () => (
    <>
      <div className="AmexExperientialEventsFilter">
        <ul className="AmexExperientialEventsFilter__citypanel">
          {(citiesToDisplay?.length > 0) && (
          <>
            <li key="EventsSearchPill" className="AmexExperientialEventsFilter__cityFilter">
              <EventsSearchPill
                onToggle={toggleLocationSelector}
                selectedFilterValues={selectedLocation}
                isOpen={locationSelectorOpen}
                filterData={filterData}
                typeOfFilter={CITY_FILTER}
                onLocationSelect={handleLocationSelect}
              />
            </li>
            <div
              aria-hidden
              className={cx('AmexExperientialEventsFilter AmexExperientialEventsFilter__citypanel__backdrop', {
                open: locationSelectorOpen,
              })}
              onClick={closeEverything}
            />
          </>
          )}
          { filterData && (
          <>
            <li key="EventsSearchCityPill">
              <EventsSearchPill
                onToggle={toggleFilterSelector}
                selectedFilterValues={selectedFilterValues}
                isOpen={filterSelectorOpen}
                filterData={filterData}
                typeOfFilter={FILTER_EVENTS}
                onFilterSelect={handleFilterSelect}
                numberOfFilter={numberOfFilter}
                onClickFilterEvents={onClickFilterEvents}
              />
            </li>
            <div
              aria-hidden
              className={cx('AmexExperientialEventsFilter AmexExperientialEventsFilter__citypanel__backdrop', {
                open: filterSelectorOpen,
              })}
              onClick={closeEverything}
            />
          </>
          ) }
        </ul>
      </div>
      {displayFilterValues && (
      <div className="AmexExperientialEventsFilter__citypanel__filterMessage" role="status">
        <p className="AmexExperientialEventsFilter__citypanel__filterMessage--text">{displayFilterValues}</p>
      </div>
      )}
    </>
  );

  if (!matchMedia.md) { return renderMobile(); }
  return renderDesktop();
};

AmexExperientialEventsFilter.propTypes = {
  handleFilterValue: PropTypes.func.isRequired,
  eventsLocations: PropTypes.arrayOf.isRequired,
  eventsMonths: PropTypes.arrayOf.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

export default AmexExperientialEventsFilter;
